import {useTranslation} from "react-i18next";
import {Dropdown, Form, Tab, Tabs} from "react-bootstrap";
import SetMatchResultModal from "../matches/SetMatchResultModal";
import {useCallback, useEffect, useRef, useState} from "react";
import {ROUND_STATES, TOURNAMENT_STATES} from "../../util/Constants";
import Button from "react-bootstrap/Button";
import {isMatchFinished} from "../../util/HelperUtils";
import {useIsMobile} from "../../hooks/useIsMobile";
import {getLoggedUser} from "../../util/SecurityUtils";
import ConfirmModal from "../common/ConfirmModal";
import {
  useFinishRound,
  useCancelRound,
  useFinishTournament,
  useSetDoubleElimination,
  useToggleBlastGeneration,
} from "../../hooks/mutations/useTournamentMutations";
import SwissRoundTab from "./tournament-detail-tabs/SwissRoundTab";
import GroupsRoundTab from "./tournament-detail-tabs/GroupsRoundTab";
import BlastRoundTab from "./tournament-detail-tabs/BlastRoundTab";
import InformationTab from "./tournament-detail-tabs/InformationTab";
import FinalPlacementTab from "./tournament-detail-tabs/FinalPlacementTab";
import GroupsOverviewTab from "./tournament-detail-tabs/GroupsOverviewTab";
import TopMatchesTab from "./tournament-detail-tabs/TopMatchesTab";
import MyMatchesTab from "./tournament-detail-tabs/MyMatchesTab";
import PlayoffBracketTab from "./tournament-detail-tabs/PlayoffBracketTab";
import AwardsCeremonyTab from "./tournament-detail-tabs/AwardsCeremonyTab";
import {useSearchParams} from "react-router-dom";
import RatingTab from "./tournament-detail-tabs/RatingTab";
import RoundTimer from "./tournament-detail-tabs/RoundTimer";
import ChatPanel from "../chat/ChatPanel";
import ChatTabTitle from "../chat/ChatTabTitle";
import {useConfetti} from "../../hooks/useConfetti";
import {useTournamentRealtime} from "../../hooks/useTournamentRealtime";
import type {Match, TournamentDetail, TournamentRound} from "../../types/domain";

interface TournamentInProgressProps {
  tournament: TournamentDetail;
}

function TournamentInProgress(props: TournamentInProgressProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ activeTab, setActiveTab] = useState("seeding");
  const [ selectedMatch, setSelectedMatch ] = useState<Match | null>(null)
  const [ showCancelRoundConfirmModal, setShowCancelRoundConfirmModal ] = useState(false)
  const [ showFinishTournamentConfirmModal, setShowFinishTournamentConfirmModal ] = useState(false)
  const [ newRoundAlert, setNewRoundAlert ] = useState<number | null>(null)
  const { fireTournamentCompleteConfetti } = useConfetti();

  // When THIS client finishes a round/tournament we (a) don't pop the "new round" alert at them —
  // they just did it — and (b) jump them to the new round / results once their data catches up.
  const finishInitiatedAtRef = useRef(0);
  const advanceOnFinishRef = useRef(false);

  // The tab key of the current (newest) round. GROUPS_PLAYOFF collapses all playoff rounds into
  // the single "round-tab-2" tab, so the "latest" there is always the playoff tab once it exists.
  const latestRoundTabKey = (): string =>
    props.tournament.type === "GROUPS_PLAYOFF" && props.tournament.rounds.length >= 2
      ? "round-tab-2"
      : "round-tab-" + props.tournament.rounds.length;

  // Live updates: refresh cached data without disrupting the user, and surface a dismissible alert
  // when a new round is announced — except to whoever just finished the round (they already know).
  useTournamentRealtime(props.tournament.id, (roundOrder) => {
    if (Date.now() - finishInitiatedAtRef.current < 5000) return;
    setNewRoundAlert(roundOrder);
  });

  const finishRoundMutation = useFinishRound();
  const cancelRoundMutation = useCancelRound();
  const finishTournamentMutation = useFinishTournament();
  const setDoubleEliminationMutation = useSetDoubleElimination();
  const toggleBlastGenerationMutation = useToggleBlastGeneration();

  const isInProgress = (round: TournamentRound | null | undefined): boolean => {
    return round?.state === ROUND_STATES.IN_PROGRESS
  }

  const selectMatch = (round: TournamentRound | undefined, match: Match) => {
    if (round && isInProgress(round)) {
      setSelectedMatch(match)
    }
  }

  const handleFinishRound = useCallback(async (tournamentId: number) => {
    finishInitiatedAtRef.current = Date.now();
    advanceOnFinishRef.current = true;
    try {
      await finishRoundMutation.mutateAsync(tournamentId);
    } catch (e) {
      finishInitiatedAtRef.current = 0;
      advanceOnFinishRef.current = false;
      throw e;
    }
  }, [finishRoundMutation])

  const handleCancelRound = useCallback(async (tournamentId: number) => {
    setShowCancelRoundConfirmModal(false)
    await cancelRoundMutation.mutateAsync(tournamentId);
  }, [cancelRoundMutation])

  const handleFinishTournament = useCallback(async (tournamentId: number) => {
    setShowFinishTournamentConfirmModal(false)
    advanceOnFinishRef.current = true;
    try {
      await finishTournamentMutation.mutateAsync(tournamentId);
    } catch (e) {
      advanceOnFinishRef.current = false;
      throw e;
    }
    fireTournamentCompleteConfetti();
  }, [fireTournamentCompleteConfetti, finishTournamentMutation])

  const handleToggleDoubleElimination = useCallback(async (tournamentId: number, enabled: boolean) => {
    await setDoubleEliminationMutation.mutateAsync({ tournamentId, enabled });
  }, [setDoubleEliminationMutation])

  const handleToggleGeneration = useCallback(async (tournamentId: number, isStopped: boolean) => {
    await toggleBlastGenerationMutation.mutateAsync({ tournamentId, isStopped });
  }, [toggleBlastGenerationMutation])

  // Read tournament shape through a ref so the tab-resolution effect below reacts ONLY to URL
  // changes (mount / navigation), never to live data changes. This is what keeps a websocket
  // "new round" from yanking the user off their current tab — they get an alert instead.
  const tournamentShapeRef = useRef({ type: props.tournament.type, roundsLength: props.tournament.rounds.length, state: props.tournament.state });
  tournamentShapeRef.current = { type: props.tournament.type, roundsLength: props.tournament.rounds.length, state: props.tournament.state };

  useEffect(() => {
    const tab = searchParams.get("tab");
    const { type, roundsLength, state } = tournamentShapeRef.current;
    // "latest" is a symbolic pointer to the current round — resolved here (on refresh/navigation)
    // so a refresh after a new round lands on the newest round, not a stale one.
    const currentRoundTab = type === "GROUPS_PLAYOFF" && roundsLength >= 2
      ? "round-tab-2"
      : "round-tab-" + roundsLength;
    if (tab) {
      setActiveTab(tab === "latest" ? currentRoundTab : tab)
    } else if (state === TOURNAMENT_STATES.FINISHED) {
      setActiveTab("finish")
    } else if (state === TOURNAMENT_STATES.IN_PROGRESS) {
      setActiveTab(currentRoundTab)
    }
  }, [searchParams, setActiveTab])

  const updateActiveTab = (newActiveTab: string | null) => {
    if (!newActiveTab) return;
    // Store the current round as "latest" so a later refresh follows along to a newer round
    // instead of pinning the URL to this round's number.
    const urlValue = newActiveTab === latestRoundTabKey() ? "latest" : newActiveTab;
    setSearchParams({"tab": urlValue})
  }

  // Jump to the newest round. Sets activeTab directly (not just the URL) because the caller is often
  // already on ?tab=latest — re-writing "latest" wouldn't change the URL, so the resolver effect
  // wouldn't fire and the tab would stay on the old round. URL is kept in sync for later refreshes.
  const goToLatestRound = () => {
    setActiveTab(latestRoundTabKey());
    setSearchParams({ tab: "latest" });
  }

  // Only the finisher (advanceOnFinishRef set in handleFinishRound/handleFinishTournament) is moved
  // along: once their refetched data reflects the change, jump to the new round — or to the results
  // tab when that finish ended the tournament. Everyone else stays put (and gets the alert instead).
  useEffect(() => {
    if (!advanceOnFinishRef.current) return;
    advanceOnFinishRef.current = false;
    // Set activeTab directly (not just the URL): the finisher is usually already on ?tab=latest, so
    // re-writing "latest" wouldn't change the URL and the resolver effect wouldn't re-run — the tab
    // would stay stuck on the old round. Setting it here switches immediately; the URL is kept in
    // sync so a later refresh still lands on the newest round / results.
    if (props.tournament.state === TOURNAMENT_STATES.FINISHED) {
      setActiveTab("finish");
      setSearchParams({ tab: "finish" });
    } else {
      goToLatestRound();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.tournament.rounds.length, props.tournament.state]);

  const selectedRound = activeTab.startsWith("round-tab-")
    ? props.tournament.rounds.find(round => "round-tab-" + round.roundOrder === activeTab)
    : undefined
  const currentInProgressRound = props.tournament.type === "GROUPS_PLAYOFF" && activeTab === "round-tab-2"
    ? props.tournament.rounds[props.tournament.rounds.length - 1]
    : selectedRound
  let canFinishRound = isInProgress(selectedRound) && (selectedRound?.matches.every((m: Match) => isMatchFinished(m.state)) ?? false)
  if (props.tournament.type === "GROUPS") {
    canFinishRound = canFinishRound && props.tournament.players.every(player => player.secondRoundGroup != null)
  }
  if (props.tournament.type === "GROUPS_PLAYOFF") {
    if (props.tournament.rounds.length === 1) {
      canFinishRound = canFinishRound && props.tournament.players.every(player => player.secondRoundGroup != null)
    } else if (props.tournament.rounds.length > 1) {
      const latestRound = props.tournament.rounds[props.tournament.rounds.length - 1]
      canFinishRound = isInProgress(latestRound) && latestRound.matches.every((m: Match) => isMatchFinished(m.state))
    }
  }
  const lastRound = props.tournament.rounds[props.tournament.rounds.length - 1];
  const hasGrandFinalOrBracketReset = lastRound?.matches.some((m: Match) =>
    m.bracketType === "GRAND_FINAL" || m.bracketType === "BRACKET_RESET"
  );
  const tournamentHasGrandFinal = props.tournament.rounds.some((r: TournamentRound) =>
    r.matches.some((m: Match) => m.bracketType === "GRAND_FINAL" || m.bracketType === "BRACKET_RESET")
  );
  const isPlayoffTournament = props.tournament.type === "GROUPS_PLAYOFF" || props.tournament.isDoubleElimination;
  const canFinishTournament = canFinishRound && (!isPlayoffTournament || hasGrandFinalOrBracketReset)
  const showFinishRoundButton = !(isPlayoffTournament && tournamentHasGrandFinal);
  const showFinishTournamentButton = !isPlayoffTournament || tournamentHasGrandFinal;
  canFinishRound = canFinishRound && (props.tournament.type !== "GROUPS" || props.tournament.rounds.length < 2)

  const buttonSize = isMobile ? "sm" : undefined;

  const loggedUserPlayerId = getLoggedUser()?.playerId;
  const doesPlayerHaveMatchesInTournament = loggedUserPlayerId != null && props.tournament.rounds.some((r: TournamentRound) =>
    r.matches.some((m: Match) => m.playerOne.id === loggedUserPlayerId || m.playerTwo.id === loggedUserPlayerId)
  )

  // The round timer is a management aid, so only organizers/admins see it. Centralized here so
  // every round tab (desktop + mobile) gates it the same way.
  const roundTimer = (round: TournamentRound) =>
    props.tournament.canManage ? <RoundTimer round={round} /> : null;

  const renderContentAsTabs = () => {
    return (
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => updateActiveTab(k)}
        id="uncontrolled-tab-example"
        variant="tabs"
        className="theme-tabs"
        style={{fontSize: isMobile ? "0.9rem" : "1rem", borderColor: 'var(--border-color)'}}
      >
        <Tab eventKey="seeding" title={t("information")}>
          <InformationTab tournament={props.tournament} />
        </Tab>
        {props.tournament.topMatches && props.tournament.topMatches.length > 0 &&
          <Tab eventKey="topMatches" title={t("topMatches")}>
            <TopMatchesTab records={props.tournament.topMatches} />
          </Tab>}
        {doesPlayerHaveMatchesInTournament &&
          <Tab eventKey="myMatches" title={t("myMatches")}>
            <MyMatchesTab tournament={props.tournament} />
          </Tab>}
        <Tab eventKey="rating" title="Rating">
          <RatingTab scores={props.tournament.finalPlacements} />
        </Tab>
        <Tab eventKey="chat" title={<ChatTabTitle tournamentId={props.tournament.id} enablePolling={activeTab !== "chat"} />}>
          <ChatPanel tournamentId={props.tournament.id} showHeader={false} asPage={false} />
        </Tab>
        {(props.tournament.type === "GROUPS" || props.tournament.type === "GROUPS_PLAYOFF") && props.tournament.rounds?.length > 0 &&
          <Tab eventKey="group-overview-1" title={t("groupOverview", {round: 1})}>
            <GroupsOverviewTab round={0} tournamentId={props.tournament.id} isActive={activeTab === "group-overview-1"} />
          </Tab>
        }
        {props.tournament.type === "GROUPS" && props.tournament.rounds?.length > 1 &&
          <Tab eventKey="group-overview-2" title={t("groupOverview", {round: 2})}>
            <GroupsOverviewTab round={1} tournamentId={props.tournament.id} isActive={activeTab === "group-overview-2"} />
          </Tab>
        }
        { isMobile ? <Tab eventKey={"disabled-round-tab"} title={t("round") + ":"} disabled={true} /> : null }
        {props.tournament.type === "GROUPS_PLAYOFF" && props.tournament.rounds.length >= 1 && (
          <Tab
            title={isMobile ? "1" : (t("roundNumber", {roundNumber: 1}) + (isInProgress(props.tournament.rounds[0]) ? t("roundInProgress") : ""))}
            key={"round-tab-1"}
            eventKey={"round-tab-1"}
          >
            {roundTimer(props.tournament.rounds[0])}
            <GroupsRoundTab
              round={props.tournament.rounds[0]}
              onMatchClick={(match: Match) => selectMatch(props.tournament.rounds[0], match)}
              players={props.tournament.players}
              tournamentId={props.tournament.id}
              canManage={props.tournament.canManage}
            />
          </Tab>
        )}
        {props.tournament.type === "GROUPS_PLAYOFF" && props.tournament.rounds.length >= 2 && (
          <Tab
            title={isMobile ? t("playoff") : (t("playoff") + (props.tournament.rounds.slice(1).some((r: TournamentRound) => isInProgress(r)) ? t("roundInProgress") : ""))}
            key={"round-tab-2"}
            eventKey={"round-tab-2"}
          >
            {roundTimer(props.tournament.rounds[props.tournament.rounds.length - 1])}
            <PlayoffBracketTab
              playoffRounds={props.tournament.rounds.slice(1)}
              tournament={props.tournament}
              onMatchClick={(match: Match) => {
                const matchRound = props.tournament.rounds.find((r: TournamentRound) => r.matches.some((m: Match) => m.id === match.id));
                selectMatch(matchRound, match);
              }}
            />
          </Tab>
        )}
        {props.tournament.type !== "GROUPS_PLAYOFF" && props.tournament.rounds.map((round: TournamentRound) => {
          const tabTitle = isMobile ? round.roundOrder :
            (t("roundNumber", {roundNumber: round.roundOrder}) + (isInProgress(round) ? t("roundInProgress") : ""))
          return (<Tab title={tabTitle} key={"round-tab-" + round.id} eventKey={"round-tab-" + round.roundOrder}>
            {roundTimer(round)}
            {props.tournament.type === "SWISS" ?
              <SwissRoundTab round={round} onMatchClick={(match: Match) => selectMatch(round, match)} tournamentId={props.tournament.id} canManage={props.tournament.canManage} />
              : props.tournament.type === "BLAST" ?
              <BlastRoundTab round={round} onMatchClick={(match: Match) => selectMatch(round, match)} players={props.tournament.players}
                             tournamentId={props.tournament.id} canManage={props.tournament.canManage} />
              : <GroupsRoundTab round={round} onMatchClick={(match: Match) => selectMatch(round, match)} players={props.tournament.players}
                                tournamentId={props.tournament.id} canManage={props.tournament.canManage} />}
          </Tab>)
        })}
        {props.tournament.state === TOURNAMENT_STATES.FINISHED ?
          <Tab eventKey="finish" title={t("finalPlacement")}>
            <FinalPlacementTab tournament={props.tournament} />
          </Tab> : null
        }
        {props.tournament.state === TOURNAMENT_STATES.FINISHED ?
          <Tab eventKey="awards" title={t("awardsCeremony")}>
            <AwardsCeremonyTab tournament={props.tournament} isActive={activeTab === "awards"} />
          </Tab> : null
        }
      </Tabs>
    )
  }

  const getTabTitle = (tabKey: string): string => {
    switch (tabKey) {
      case "seeding":
        return t("information");
      case "topMatches":
        return t("topMatches");
      case "group-overview-1":
        return t("groupOverview", {round: 1});
      case "group-overview-2":
        return t("groupOverview", {round: 2});
      case "finish":
        return t("finalPlacement");
      case "awards":
        return t("awardsCeremony");
      case "myMatches":
        return t("myMatches");
      case "rating":
        return "Rating";
      case "chat":
        return t("chat");
      default:
        if (tabKey.startsWith("round-tab-")) {
          const roundOrder = tabKey.split("-")[2];
          if (props.tournament.type === "GROUPS_PLAYOFF") {
            if (roundOrder === "1") {
              return t("roundNumber", {roundNumber: 1}) + " (" + t("groups") + ")" + (isInProgress(props.tournament.rounds[0]) ? t("roundInProgress") : "");
            } else if (roundOrder === "2") {
              return t("playoff") + (props.tournament.rounds.slice(1).some((r: TournamentRound) => isInProgress(r)) ? t("roundInProgress") : "");
            }
          }
          return t("roundNumber", {roundNumber: roundOrder}) + (isInProgress(props.tournament.rounds.find((r: TournamentRound) => r.roundOrder.toString() === roundOrder)) ? t("roundInProgress") : "");
        }
        return tabKey;
    }
  }

  const renderContentAsDropdown = () => {
    return (
      <>
        <Dropdown>
          <Dropdown.Toggle
            id="dropdown-basic"
            className="w-100 mb-3"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              borderColor: 'var(--border-color)',
              color: 'var(--text-primary)'
            }}
          >
            {getTabTitle(activeTab)}
          </Dropdown.Toggle>

          <Dropdown.Menu style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
            <Dropdown.Item onClick={() => updateActiveTab("seeding")}>{t("information")}</Dropdown.Item>
            {props.tournament.topMatches && props.tournament.topMatches.length > 0 &&
              <Dropdown.Item onClick={() => updateActiveTab("topMatches")}>{t("topMatches")}</Dropdown.Item>
            }
            {doesPlayerHaveMatchesInTournament &&
              <Dropdown.Item onClick={() => updateActiveTab("myMatches")}>{t("myMatches")}</Dropdown.Item>
            }
            <Dropdown.Item onClick={() => updateActiveTab("rating")}>Rating</Dropdown.Item>
            <Dropdown.Item onClick={() => updateActiveTab("chat")}>
              <ChatTabTitle tournamentId={props.tournament.id} enablePolling={activeTab !== "chat"} />
            </Dropdown.Item>
            {(props.tournament.type === "GROUPS" || props.tournament.type === "GROUPS_PLAYOFF") && props.tournament.rounds?.length > 0 &&
              <Dropdown.Item onClick={() => updateActiveTab("group-overview-1")}>{t("groupOverview", {round: 1})}</Dropdown.Item>
            }
            {props.tournament.type === "GROUPS" && props.tournament.rounds?.length > 1 &&
              <Dropdown.Item onClick={() => updateActiveTab("group-overview-2")}>{t("groupOverview", {round: 2})}</Dropdown.Item>
            }
            <Dropdown.Divider />
            { props.tournament.state === TOURNAMENT_STATES.IN_PROGRESS &&
              <Dropdown.Item onClick={() => {
                if (props.tournament.type === "GROUPS_PLAYOFF" && props.tournament.rounds.length >= 2) {
                  updateActiveTab("round-tab-2")
                } else {
                  updateActiveTab("round-tab-" + props.tournament.rounds.length)
                }
              }}>{t("currentRound")}</Dropdown.Item>
            }
            <Dropdown drop="end">
              <Dropdown.Toggle as="div" className="dropdown-item">
                {t(props.tournament.state === TOURNAMENT_STATES.IN_PROGRESS ? "otherRounds" : "rounds")}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {props.tournament.type === "GROUPS_PLAYOFF" ? (
                  <>
                    <Dropdown.Item onClick={() => updateActiveTab("round-tab-1")} key="round-tab-1">
                      {t("roundNumber", {roundNumber: 1}) + " (" + t("groups") + ")" + (isInProgress(props.tournament.rounds[0]) ? t("roundInProgress") : "")}
                    </Dropdown.Item>
                    {props.tournament.rounds.length >= 2 && (
                      <Dropdown.Item onClick={() => updateActiveTab("round-tab-2")} key="round-tab-2">
                        {t("playoff") + (props.tournament.rounds.slice(1).some((r: TournamentRound) => isInProgress(r)) ? t("roundInProgress") : "")}
                      </Dropdown.Item>
                    )}
                  </>
                ) : (
                  props.tournament.rounds.map((round: TournamentRound) => {
                    const tabTitle = (t("roundNumber", {roundNumber: round.roundOrder}) + (isInProgress(round) ? t("roundInProgress") : ""))
                    return <Dropdown.Item onClick={() => updateActiveTab("round-tab-" + round.roundOrder)} key={"round-tab-" + round.id}>{tabTitle}</Dropdown.Item>
                  })
                )}
              </Dropdown.Menu>
            </Dropdown>
            { props.tournament.state === TOURNAMENT_STATES.FINISHED && <Dropdown.Divider /> }
            { props.tournament.state === TOURNAMENT_STATES.FINISHED &&
              <Dropdown.Item onClick={() => updateActiveTab("finish")}>{t("finalPlacement")}</Dropdown.Item>
            }
            { props.tournament.state === TOURNAMENT_STATES.FINISHED &&
              <Dropdown.Item onClick={() => updateActiveTab("awards")}>{t("awardsCeremony")}</Dropdown.Item>
            }
          </Dropdown.Menu>
        </Dropdown>
        { activeTab === "seeding" && <InformationTab tournament={props.tournament} />}
        { activeTab === "topMatches" && <TopMatchesTab records={props.tournament.topMatches} />}
        { activeTab === "myMatches" && <MyMatchesTab tournament={props.tournament} />}
        { activeTab === "rating" && <RatingTab scores={props.tournament.rounds[props.tournament.rounds.length - 1].scores} />}
        { activeTab === "chat" && <ChatPanel tournamentId={props.tournament.id} showHeader={false} asPage={false} />}
        { activeTab === "group-overview-1" && <GroupsOverviewTab round={0} tournamentId={props.tournament.id} isActive={activeTab === "group-overview-1"} />}
        { activeTab === "group-overview-2" && <GroupsOverviewTab round={1} tournamentId={props.tournament.id} isActive={activeTab === "group-overview-2"} />}
        {props.tournament.type === "GROUPS_PLAYOFF" ? (
          <>
            {activeTab === "round-tab-1" && props.tournament.rounds.length >= 1 && (
              <>
                {roundTimer(props.tournament.rounds[0])}
                <GroupsRoundTab
                  round={props.tournament.rounds[0]}
                  onMatchClick={(match: Match) => selectMatch(props.tournament.rounds[0], match)}
                  players={props.tournament.players}
                  tournamentId={props.tournament.id}
                  canManage={props.tournament.canManage}
                />
              </>
            )}
            {activeTab === "round-tab-2" && props.tournament.rounds.length >= 2 && (
              <>
                {roundTimer(props.tournament.rounds[props.tournament.rounds.length - 1])}
                <PlayoffBracketTab
                  playoffRounds={props.tournament.rounds.slice(1)}
                  tournament={props.tournament}
                  onMatchClick={(match: Match) => {
                    const matchRound = props.tournament.rounds.find((r: TournamentRound) => r.matches.some((m: Match) => m.id === match.id));
                    selectMatch(matchRound, match);
                  }}
                />
              </>
            )}
          </>
        ) : (
          props.tournament.rounds.map((round: TournamentRound) => {
            return activeTab === ("round-tab-" + round.roundOrder) &&
              (<>
                {roundTimer(round)}
                {props.tournament.type === "SWISS" ?
                  <SwissRoundTab round={round} onMatchClick={(match: Match) => selectMatch(round, match)} tournamentId={props.tournament.id} canManage={props.tournament.canManage} />
                  : props.tournament.type === "BLAST" ?
                  <BlastRoundTab round={round} onMatchClick={(match: Match) => selectMatch(round, match)} players={props.tournament.players}
                                 tournamentId={props.tournament.id} canManage={props.tournament.canManage} />
                  : <GroupsRoundTab round={round} onMatchClick={(match: Match) => selectMatch(round, match)} players={props.tournament.players}
                                    tournamentId={props.tournament.id} canManage={props.tournament.canManage} />}
              </>)
          })
        )}
        { activeTab === "finish" && <FinalPlacementTab tournament={props.tournament} />}
        { activeTab === "awards" && <AwardsCeremonyTab tournament={props.tournament} isActive={true} />}
      </>

    );
  }

  return (
    <div>
      { props.tournament.canManage && props.tournament.state !== TOURNAMENT_STATES.FINISHED ? <div className={"d-flex justify-content-end align-items-center mt-3" + (isMobile ? " mb-3" : "")}>
          {props.tournament.type === "GROUPS_PLAYOFF" && props.tournament.rounds.length === 1 && (
            <Form.Check
              type="switch"
              id="double-elimination-switch"
              label={t("doubleElimination")}
              checked={props.tournament.isDoubleElimination || false}
              onChange={(e) => handleToggleDoubleElimination(props.tournament.id, e.target.checked)}
              className="me-3"
              style={{ color: 'var(--text-primary)' }}
            />
          )}
          {props.tournament.type === "BLAST" ? (
            <Button
              variant={props.tournament.generationStopped ? "success" : "warning"}
              onClick={() => handleToggleGeneration(props.tournament.id, props.tournament.generationStopped)}
              className="me-3"
              size={buttonSize}
            >
              {props.tournament.generationStopped ? t("resumeGeneration") : t("stopGeneration")}
            </Button>
          ) : showFinishRoundButton && (
            <Button variant="success" onClick={() => handleFinishRound(props.tournament.id)} disabled={!canFinishRound} className="me-3" size={buttonSize}>
              {t("finishRound")}
            </Button>
          )}
          <Button variant="danger" onClick={() => setShowCancelRoundConfirmModal(true)} disabled={!isInProgress(currentInProgressRound)} className="me-3" size={buttonSize}>
            {t("cancelRound")}
          </Button>
          {showFinishTournamentButton && (
            <Button variant="outline-info" onClick={() => setShowFinishTournamentConfirmModal(true)} disabled={!canFinishTournament} size={buttonSize}>
              {t("finishTournament")}
            </Button>
          )}
      </div> : null}
      { isMobile ? renderContentAsDropdown() : renderContentAsTabs() }
      {selectedMatch ? <SetMatchResultModal isOpen={true} match={selectedMatch} closeModal={() => setSelectedMatch(null)} canManage={props.tournament.canManage} /> : null}
      {showCancelRoundConfirmModal ? <ConfirmModal title={t("cancelRound")} message={t("cancelRoundConfirmation")}
        onCancel={() => setShowCancelRoundConfirmModal(false)} onConfirm={() => handleCancelRound(props.tournament.id)}/> : null}
      {showFinishTournamentConfirmModal ? <ConfirmModal title={t("finishTournament")} message={t("finishTournamentConfirmation")}
        onCancel={() => setShowFinishTournamentConfirmModal(false)} onConfirm={() => handleFinishTournament(props.tournament.id)}/> : null}
      {newRoundAlert != null ? (
        <div className="new-round-alert" role="status">
          <div className="new-round-alert-text">
            <span className="new-round-alert-title">{t("newRoundStartedTitle")}</span>
            <span className="new-round-alert-body">{t("newRoundStartedBody", {round: newRoundAlert})}</span>
          </div>
          <Button variant="success" size="sm" onClick={() => { goToLatestRound(); setNewRoundAlert(null); }}>
            {t("goToNewRound")}
          </Button>
          <button type="button" className="new-round-alert-close" aria-label={t("cancel")} onClick={() => setNewRoundAlert(null)}>×</button>
        </div>
      ) : null}
    </div>
  );
}

export default TournamentInProgress
